import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { CURRENCY_ICON } from "@app/constants/currencies";
import { Currency } from "@app/types/currencies";
import { CONVERTER_AMOUNT_VALUES } from "./constants";
import { formatToFinancialAmountString } from "./formatters";

type Props = {
  baseCurrency: Currency;
  convertCurrency: Currency;
  rate: number;
};

export const Table: React.FC<Props> = ({
  baseCurrency,
  convertCurrency,
  rate,
}) => {
  const { t } = useTranslation("currency-converter");
  const amountValues = CONVERTER_AMOUNT_VALUES.map((amount) => ({
    base: formatToFinancialAmountString(amount),
    convert: formatToFinancialAmountString((amount * rate).toFixed(2)),
  }));

  return (
    <div
      className={classNames(
        "grow rounded-lg bg-dark px-8 py-15 text-primary-content",
        "lg:px-10 lg:pb-15 lg:pt-13",
        "xl:px-15",
      )}
    >
      <h5 className={classNames("mb-6 text-medium", "lg:mb-10")}>
        {`${t(`convertTableTitle.from.${baseCurrency}`, { keySeparator: "." })} ${t(`convertTableTitle.to.${convertCurrency}`, { keySeparator: "." })}`}
      </h5>
      <div
        className={classNames(
          "mb-3 flex items-center justify-between gap-5 text-regular",
        )}
      >
        {[baseCurrency, convertCurrency].map((currency, index) => (
          <div
            key={`${currency}-${index}`}
            className="flex items-center gap-x-2.5"
          >
            <div className="flex h-6 w-6 overflow-hidden rounded-full">
              {React.createElement(CURRENCY_ICON[currency] ?? null)}
            </div>
            <div>{currency}</div>
          </div>
        ))}
      </div>
      {amountValues.map((amount) => (
        <div
          key={amount.base}
          className={classNames(
            "flex items-center justify-between gap-5 border-b border-solid border-divider py-2.5 text-regular text-base-400",
            "lg:py-3",
            "last-of-type:border-0",
          )}
        >
          <div>{`${amount.base} ${baseCurrency}`}</div>
          <div>{`${amount.convert} ${convertCurrency}`}</div>
        </div>
      ))}
    </div>
  );
};
